<template>
	<div
		ref="rootEl"
		class="clipper-player group relative w-full aspect-video min-w-0 overflow-hidden rounded-lg bg-black select-none"
		style="aspect-ratio: 16 / 9"
		@click="onSurfaceClick"
		@dblclick.prevent="toggleFullscreen"
	>
		<video
			ref="videoEl"
			class="absolute inset-0 h-full w-full bg-black"
			playsinline
			crossorigin="anonymous"
			preload="auto"
			:muted="isMuted"
		/>

		<!-- Center state overlays -->
		<div
			v-if="!hasSource"
			class="absolute inset-0 flex items-center justify-center text-tiny text-surface-8"
		>
			<slot name="empty">Nothing to play yet</slot>
		</div>
		<div
			v-else-if="fatalError"
			class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 px-6 text-center"
		>
			<p class="m-0 text-sm text-surface-text-2">{{ fatalError }}</p>
			<FormButton type="secondary" size="sm" @click.stop="retry">Try again</FormButton>
		</div>
		<template v-else>
			<div
				v-if="isBuffering && !isPaused"
				class="pointer-events-none absolute inset-0 flex items-center justify-center"
			>
				<span class="clipper-spinner" />
			</div>
			<button
				v-else-if="isPaused"
				type="button"
				class="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white ring-1 ring-white/20 transition hover:bg-main"
				aria-label="Play"
				@click.stop="play"
			>
				<svg width="22" height="22" viewBox="0 0 16 16" aria-hidden="true">
					<path d="M5.5 3.2v9.6c0 .56.61.91 1.09.62l8.3-4.8a.7.7 0 0 0 0-1.24l-8.3-4.8A.7.7 0 0 0 5.5 3.2Z" fill="currentColor" />
				</svg>
			</button>
		</template>

		<!-- Unmute hint after a muted autoplay fallback -->
		<button
			v-if="autoplayMuted && isMuted && hasSource && !fatalError"
			type="button"
			class="absolute left-3 top-3 flex items-center gap-2 rounded-md bg-black/70 px-3 py-1.5 text-tiny text-white ring-1 ring-white/15 hover:bg-black/90"
			@click.stop="unmute"
		>
			<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6h2.5L8 3v10L4.5 10H2V6Zm9.2-.2 2.8 2.8m0-2.8-2.8 2.8" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round" /></svg>
			Tap to unmute
		</button>

		<!-- State badge: one element with a reserved slot so switching modes never reflows -->
		<div
			class="pointer-events-none absolute right-3 top-3 flex h-6 items-center"
			:class="{ invisible: !hasSource || fatalError }"
			aria-live="polite"
		>
			<span
				class="inline-flex h-6 min-w-[64px] items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[11px] font-semibold uppercase tracking-wide tabular-nums"
				:class="badge.cls"
			>
				<span v-if="badge.dot" class="h-1.5 w-1.5 rounded-full bg-white" />
				<span>{{ badge.mode }}</span>
				<span v-if="badge.detail" class="font-medium normal-case tracking-normal">· {{ badge.detail }}</span>
			</span>
		</div>

		<!-- Control bar -->
		<div
			v-if="hasSource && !fatalError"
			class="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6 text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-within:opacity-100"
			:class="{ '!opacity-100': isPaused }"
			@click.stop
		>
			<button
				type="button"
				class="flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
				:aria-label="isPaused ? 'Play' : 'Pause'"
				@click="togglePlay"
			>
				<svg v-if="isPaused" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
					<path d="M5.5 3.2v9.6c0 .56.61.91 1.09.62l8.3-4.8a.7.7 0 0 0 0-1.24l-8.3-4.8A.7.7 0 0 0 5.5 3.2Z" fill="currentColor" />
				</svg>
				<svg v-else width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
					<rect x="3.5" y="3" width="3.2" height="10" rx="0.8" fill="currentColor" />
					<rect x="9.3" y="3" width="3.2" height="10" rx="0.8" fill="currentColor" />
				</svg>
			</button>
			<button
				type="button"
				class="flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
				:aria-label="isMuted ? 'Unmute' : 'Mute'"
				@click="toggleMute"
			>
				<svg v-if="isMuted" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6h2.5L8 3v10L4.5 10H2V6Zm9.2-.2 2.8 2.8m0-2.8-2.8 2.8" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round" /></svg>
				<svg v-else width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6h2.5L8 3v10L4.5 10H2V6Zm8.5-1a4 4 0 0 1 0 6m1.8-8a6.5 6.5 0 0 1 0 10" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round" /></svg>
			</button>
			<span class="ml-1 inline-block min-w-[64px] font-mono text-[12px] tabular-nums text-surface-text-2" aria-live="off">
				{{ wallTimeLabel }}
			</span>
			<span class="flex-1" />
			<!-- Always occupies its slot; hidden while already live -->
			<button
				type="button"
				class="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
				:class="{ invisible: currentMode === 'live' || !liveAvailable }"
				:disabled="currentMode === 'live' || !liveAvailable"
				:tabindex="currentMode === 'live' || !liveAvailable ? -1 : 0"
				title="Jump to live (End)"
				@click="goLive"
			>
				<span class="h-1.5 w-1.5 rounded-full bg-error" />
				Jump to live
			</button>
			<button
				type="button"
				class="flex h-8 w-8 items-center justify-center rounded-md hover:bg-white/10"
				aria-label="Toggle fullscreen"
				@click="toggleFullscreen"
			>
				<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 6V2.5H6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round" stroke-linejoin="round" /></svg>
			</button>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import Hls from 'hls.js';
import FormButton from '@/components/Atoms/FormButton.vue';
import {
	livePlaylistUrl,
	windowPlaylistUrl,
	rangeAt,
	nextRangeStart,
	formatLocalTime,
	safeFileStem,
} from './dvrUtils';

const props = defineProps({
	baseUrl: { type: String, default: '' },
	// 'live' plays the rolling live playlist, 'window' plays a VOD window that
	// starts at `from` and runs to the end of the containing range (or `from +
	// duration` when a duration is given, e.g. selection preview).
	mode: { type: String, default: 'live' },
	from: { type: Number, default: null },
	duration: { type: Number, default: null },
	loop: { type: Boolean, default: false },
	muted: { type: Boolean, default: false },
	// Recorded ranges ({ start, end } seconds) used for range hopping.
	ranges: { type: Array, default: () => [] },
	// Whether the stream is currently live (enables "Jump to live" in replay).
	liveAvailable: { type: Boolean, default: false },
	// Latest recorded second; used for the "behind live" readout in replay.
	liveEdge: { type: Number, default: null },
	// Used for the screenshot file name.
	streamName: { type: String, default: '' },
	autoplay: { type: Boolean, default: true },
});

const emit = defineEmits(['time', 'live', 'ended', 'error', 'ready', 'playing', 'source-change', 'muted-change', 'segments']);

const HLS_CONFIG = {
	enableWorker: true,
	lowLatencyMode: true,
	progressive: true,
	maxBufferHole: 0,
	maxMaxBufferLength: 60,
	backBufferLength: 90,
	startLevel: -1,
};

const rootEl = ref(null);
const videoEl = ref(null);

const isPaused = ref(true);
const isBuffering = ref(false);
const isMuted = ref(props.muted);
const autoplayMuted = ref(false);
const fatalError = ref('');
const wallTime = ref(null);

// Active source (may differ from props after an internal range hop).
const currentMode = ref(props.mode);
const currentFrom = ref(props.from);
const currentEnd = ref(null);

const hasSource = computed(() => !!props.baseUrl && (currentMode.value === 'live' || typeof currentFrom.value === 'number'));
const wallTimeLabel = computed(() => (typeof wallTime.value === 'number' ? formatLocalTime(wallTime.value) : '--:--:--'));

// Seconds behind the live edge while replaying (null when unknown).
const lagSec = computed(() => {
	if (currentMode.value === 'live' || typeof props.liveEdge !== 'number' || typeof wallTime.value !== 'number') return null;
	return Math.max(0, Math.round(props.liveEdge - wallTime.value));
});

function lagLabel(sec) {
	if (sec < 60) return `${sec}s`;
	const m = Math.floor(sec / 60);
	if (m < 60) return `${m}m ${String(sec % 60).padStart(2, '0')}s`;
	const h = Math.floor(m / 60);
	return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

// Single state model: LIVE (red), REPLAY with lag (neutral), LOOPING CLIP (blue).
const badge = computed(() => {
	if (currentMode.value === 'live') return { mode: 'Live', detail: '', cls: 'bg-error text-white', dot: true };
	if (props.loop) return { mode: 'Looping clip', detail: '', cls: 'bg-main text-white', dot: false };
	const lag = lagSec.value;
	return {
		mode: 'Replay',
		detail: lag === null || !props.liveAvailable ? '' : `${lagLabel(lag)} behind`,
		cls: 'bg-surface-3/90 text-surface-text-1',
		dot: false,
	};
});

let hls = null;
let nativeHls = false;
// The `from` value the parent last learned about (via props, seekWall or
// source-change) and the duration the current playlist was built with.
let syncedFrom = props.from;
let loadedDuration = props.duration;
let pdtBase = null; // seconds epoch of fragStart
let pdtFragStart = 0; // media time matching pdtBase
let pendingSeekWall = null;
let tickTimer = null;
let mediaErrorRecoveries = 0;
let lastErrorRetryAt = 0;
let loadToken = 0;

function currentUrl() {
	if (!props.baseUrl) return '';
	if (currentMode.value === 'live') return livePlaylistUrl(props.baseUrl);
	if (typeof currentFrom.value !== 'number') return '';
	const range = rangeAt(props.ranges, currentFrom.value);
	let end = range ? range.end : currentFrom.value + 60;
	if (typeof props.duration === 'number' && props.duration > 0) {
		end = Math.min(end, currentFrom.value + props.duration);
	}
	currentEnd.value = end;
	// Playlists snap to ~6s segment boundaries; ask for a little extra so the
	// requested end is actually reachable.
	return windowPlaylistUrl(props.baseUrl, currentFrom.value, end - currentFrom.value + 1);
}

function resetTiming() {
	pdtBase = null;
	pdtFragStart = 0;
}

function destroyHls() {
	if (hls) {
		hls.destroy();
		hls = null;
	}
	const v = videoEl.value;
	if (v && nativeHls) {
		v.removeAttribute('src');
		v.load();
	}
	nativeHls = false;
}

function updatePdtFromFragment(frag) {
	if (!frag || typeof frag.programDateTime !== 'number') return;
	pdtBase = frag.programDateTime / 1000;
	pdtFragStart = frag.start;
	applyPendingSeek();
}

function applyPendingSeek() {
	const v = videoEl.value;
	if (!v || pendingSeekWall === null || pdtBase === null) return;
	const target = pdtFragStart + (pendingSeekWall - pdtBase);
	pendingSeekWall = null;
	if (Number.isFinite(target) && target > 0) {
		v.currentTime = target;
	}
}

function computeWallTime() {
	const v = videoEl.value;
	if (!v) return null;
	if (nativeHls && typeof v.getStartDate === 'function') {
		const d = v.getStartDate();
		if (d && !Number.isNaN(d.getTime())) return d.getTime() / 1000 + v.currentTime;
	}
	if (hls) {
		const d = hls.playingDate;
		if (d && !Number.isNaN(d.getTime())) return d.getTime() / 1000;
	}
	if (pdtBase !== null) return pdtBase + (v.currentTime - pdtFragStart);
	return null;
}

function tick() {
	const t = computeWallTime();
	if (typeof t === 'number' && Number.isFinite(t)) {
		wallTime.value = t;
		emit('time', t);
	}
	// Window playlists can run past the requested end by up to one segment.
	if (currentMode.value === 'window' && typeof currentEnd.value === 'number' && typeof t === 'number' && t >= currentEnd.value - 0.05) {
		handleEnded();
	}
}

function startTicking() {
	stopTicking();
	tickTimer = setInterval(tick, 100);
}

function stopTicking() {
	clearInterval(tickTimer);
	tickTimer = null;
}

function tryPlay() {
	const v = videoEl.value;
	if (!v) return;
	const p = v.play();
	if (p && typeof p.catch === 'function') {
		p.catch((err) => {
			if (err && err.name === 'NotAllowedError' && !v.muted) {
				isMuted.value = true;
				autoplayMuted.value = true;
				v.muted = true;
				emit('muted-change', true);
				v.play().catch(() => {});
			}
		});
	}
}

function load() {
	const v = videoEl.value;
	if (!v) return;
	loadToken += 1;
	const token = loadToken;
	destroyHls();
	resetTiming();
	fatalError.value = '';
	mediaErrorRecoveries = 0;
	const url = currentUrl();
	syncedFrom = currentFrom.value;
	loadedDuration = props.duration;
	emit('source-change', { mode: currentMode.value, from: currentFrom.value, end: currentEnd.value });
	if (!url) return;
	pendingSeekWall = currentMode.value === 'window' ? currentFrom.value : null;

	if (Hls.isSupported()) {
		hls = new Hls(HLS_CONFIG);
		hls.on(Hls.Events.MANIFEST_PARSED, () => {
			if (token !== loadToken) return;
			emit('ready');
			if (props.autoplay) tryPlay();
		});
		hls.on(Hls.Events.LEVEL_LOADED, (_e, data) => {
			if (token !== loadToken) return;
			const frags = data?.details?.fragments || [];
			updatePdtFromFragment(frags[0]);
			// Segment boundaries let the panel predict the exported window exactly.
			const starts = [];
			for (const f of frags) {
				if (typeof f?.programDateTime === 'number') starts.push(f.programDateTime / 1000);
			}
			if (starts.length) emit('segments', starts);
		});
		hls.on(Hls.Events.FRAG_CHANGED, (_e, data) => {
			if (token !== loadToken) return;
			updatePdtFromFragment(data?.frag);
		});
		hls.on(Hls.Events.ERROR, (_e, data) => {
			if (token !== loadToken) return;
			if (!data?.fatal) return;
			if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
				if (mediaErrorRecoveries < 3) {
					mediaErrorRecoveries += 1;
					hls?.recoverMediaError();
					return;
				}
			}
			if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
				const status = data?.response?.code;
				if (currentMode.value === 'window' && status === 404) {
					fatalError.value = 'This part of the recording is no longer available.';
				} else if (currentMode.value === 'live' && (status === 404 || data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR)) {
					fatalError.value = 'The stream is offline. Live playback will resume when it comes back.';
				} else {
					fatalError.value = 'Playback failed to load. Check your connection and try again.';
				}
				// Auto-retry once after a short pause for live sources.
				const now = Date.now();
				if (currentMode.value === 'live' && now - lastErrorRetryAt > 15000) {
					lastErrorRetryAt = now;
					setTimeout(() => {
						if (token === loadToken && fatalError.value) load();
					}, 8000);
				}
			} else {
				fatalError.value = 'Playback ran into a problem. Try again.';
			}
			emit('error', { message: fatalError.value, details: data.details });
			hls?.stopLoad();
		});
		hls.attachMedia(v);
		hls.loadSource(url);
	} else if (v.canPlayType('application/vnd.apple.mpegurl')) {
		nativeHls = true;
		v.src = url;
		v.addEventListener('loadedmetadata', () => {
			if (token !== loadToken) return;
			emit('ready');
			if (pendingSeekWall !== null && typeof v.getStartDate === 'function') {
				const d = v.getStartDate();
				if (d && !Number.isNaN(d.getTime())) {
					v.currentTime = Math.max(0, pendingSeekWall - d.getTime() / 1000);
				}
				pendingSeekWall = null;
			}
			if (props.autoplay) tryPlay();
		}, { once: true });
	} else {
		fatalError.value = 'This browser cannot play the stream.';
		emit('error', { message: fatalError.value });
	}
}

function handleEnded() {
	const v = videoEl.value;
	if (currentMode.value !== 'window') {
		emit('ended');
		return;
	}
	if (props.loop && typeof currentFrom.value === 'number') {
		pendingSeekWall = currentFrom.value;
		applyPendingSeek();
		if (v && v.paused) tryPlay();
		return;
	}
	const endedAt = currentEnd.value;
	emit('ended', { at: endedAt });
	// Explicit preview windows stop at their end.
	if (typeof props.duration === 'number' && props.duration > 0) {
		if (v) v.pause();
		return;
	}
	const range = rangeAt(props.ranges, endedAt);
	if (range && range.end > endedAt + 2) {
		// The range kept growing while we played (live recording): continue.
		currentFrom.value = endedAt;
		load();
		return;
	}
	const next = nextRangeStart(props.ranges, endedAt);
	if (next !== null) {
		currentFrom.value = next;
		load();
		return;
	}
	if (props.liveAvailable) {
		goLive();
	} else if (v) {
		v.pause();
	}
}

function goLive() {
	currentMode.value = 'live';
	currentFrom.value = null;
	currentEnd.value = null;
	emit('live', true);
	load();
}

function seekWall(t) {
	if (typeof t !== 'number' || !Number.isFinite(t)) return;
	const v = videoEl.value;
	// Fast path: the target sits inside the current window playlist.
	if (
		currentMode.value === 'window'
		&& typeof currentFrom.value === 'number'
		&& typeof currentEnd.value === 'number'
		&& t >= currentFrom.value
		&& t < currentEnd.value
		&& pdtBase !== null
		&& v
	) {
		syncedFrom = t;
		v.currentTime = pdtFragStart + (t - pdtBase);
		return;
	}
	currentMode.value = 'window';
	currentFrom.value = t;
	emit('live', false);
	load();
}

function play() {
	tryPlay();
}

function pause() {
	videoEl.value?.pause();
}

function togglePlay() {
	if (!videoEl.value) return;
	if (videoEl.value.paused) tryPlay();
	else videoEl.value.pause();
}

function toggleMute() {
	setMuted(!isMuted.value);
}

function unmute() {
	setMuted(false);
}

function setMuted(m) {
	isMuted.value = m;
	autoplayMuted.value = false;
	if (videoEl.value) videoEl.value.muted = m;
	emit('muted-change', m);
}

function retry() {
	load();
}

function onSurfaceClick() {
	if (!hasSource.value || fatalError.value) return;
	togglePlay();
}

function toggleFullscreen() {
	const el = rootEl.value;
	if (!el) return;
	if (document.fullscreenElement) {
		document.exitFullscreen?.();
	} else if (el.requestFullscreen) {
		el.requestFullscreen().catch(() => {});
	} else if (videoEl.value?.webkitEnterFullscreen) {
		videoEl.value.webkitEnterFullscreen();
	}
}

// Screenshot of the current frame. Resolves { ok, error? }.
async function captureFrame() {
	const v = videoEl.value;
	if (!v || !v.videoWidth) return { ok: false, error: 'No video frame is available yet.' };
	try {
		const canvas = document.createElement('canvas');
		canvas.width = v.videoWidth;
		canvas.height = v.videoHeight;
		const ctx = canvas.getContext('2d');
		ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
		const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
		if (!blob) throw new Error('empty');
		const stamp = typeof wallTime.value === 'number' ? wallTime.value : Date.now() / 1000;
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = `${safeFileStem(props.streamName, stamp)}.png`;
		a.rel = 'noopener';
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(a.href), 5000);
		return { ok: true, at: stamp };
	} catch (e) {
		return { ok: false, error: 'The screenshot could not be captured in this browser. Try pausing the video first.' };
	}
}

function onPlay() {
	isPaused.value = false;
	emit('playing', true);
	startTicking();
}

function onPause() {
	isPaused.value = true;
	emit('playing', false);
	stopTicking();
	tick();
}

function onWaiting() {
	isBuffering.value = true;
}

function onPlayingOrCanPlay() {
	isBuffering.value = false;
}

function onVideoEnded() {
	handleEnded();
}

function onVolumeChange() {
	const v = videoEl.value;
	if (v && v.muted !== isMuted.value) {
		isMuted.value = v.muted;
		emit('muted-change', v.muted);
	}
}

onMounted(() => {
	const v = videoEl.value;
	v.muted = isMuted.value;
	v.addEventListener('play', onPlay);
	v.addEventListener('pause', onPause);
	v.addEventListener('waiting', onWaiting);
	v.addEventListener('playing', onPlayingOrCanPlay);
	v.addEventListener('canplay', onPlayingOrCanPlay);
	v.addEventListener('ended', onVideoEnded);
	v.addEventListener('volumechange', onVolumeChange);
	if (hasSource.value) load();
});

onBeforeUnmount(() => {
	stopTicking();
	loadToken += 1;
	const v = videoEl.value;
	if (v) {
		v.removeEventListener('play', onPlay);
		v.removeEventListener('pause', onPause);
		v.removeEventListener('waiting', onWaiting);
		v.removeEventListener('playing', onPlayingOrCanPlay);
		v.removeEventListener('canplay', onPlayingOrCanPlay);
		v.removeEventListener('ended', onVideoEnded);
		v.removeEventListener('volumechange', onVolumeChange);
	}
	destroyHls();
});

watch(() => props.baseUrl, () => {
	currentMode.value = props.mode;
	currentFrom.value = props.from;
	if (videoEl.value) load();
});

watch([() => props.mode, () => props.from, () => props.duration], ([mode, from, duration]) => {
	// The parent usually mirrors our own 'live' / 'source-change' / seekWall
	// state back into props; skip the reload when nothing actually changed.
	const sameSource = mode === currentMode.value
		&& (mode === 'live' || from === syncedFrom)
		&& duration === loadedDuration;
	if (sameSource) return;
	currentMode.value = mode;
	currentFrom.value = mode === 'live' ? null : from;
	syncedFrom = currentFrom.value;
	loadedDuration = duration;
	if (videoEl.value) load();
});

watch(() => props.muted, (m) => {
	if (m !== isMuted.value) setMuted(m);
});

defineExpose({ play, pause, togglePlay, seekWall, goLive, captureFrame, setMuted });
</script>

<style scoped>
/* The box owns its height: the video is absolutely positioned inside so
   swapping sources (live <-> replay) can never collapse or grow it. */
@supports not (aspect-ratio: 16 / 9) {
	.clipper-player {
		height: 0;
		padding-bottom: 56.25%;
	}
}

.clipper-spinner {
	width: 36px;
	height: 36px;
	border-radius: 9999px;
	border: 3px solid rgba(255, 255, 255, 0.25);
	border-top-color: #fff;
	animation: clipper-spin 0.8s linear infinite;
}

@keyframes clipper-spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
